<template>
  <div>
    <b-card class="health-assessments-list-card achievements-list-card">
      <div class="gradient-line-wrapper"></div>
      <div
        class="table-header d-flex flex-wrap justify-content-between align-items-center mb-2"
      >
        <div>
          <h5 class="mb-0">Achievement History</h5>
          <small class="text-muted"
            >Manage student achievement records and history details</small
          >
        </div>
      </div>

      <b-row class="align-items-center filter-row-single-line mb-2 no-wrap-row">
        <b-col xl="5" lg="5" md="6" sm="12" cols="12">
          <b-form-input
            v-model="searchQuery"
            placeholder="Search by student name, father name, batch, field, institute, achievement..."
            @input="onSearchInput"
            class="filter-control"
            debounce="400"
          />
        </b-col>
        <b-col xl="7" lg="7" md="6" sm="12" cols="12" class="text-right">
          <div
            class="d-flex align-items-center justify-content-end action-buttons-group"
          >
            <b-button
              variant="gradient-success"
              @click="printReport"
              class="action-btn-filter"
              :disabled="dataLoading || displayedAchievements.length === 0"
            >
              <feather-icon icon="PrinterIcon" size="16" class="mr-1" />
              Print Report
            </b-button>
            <b-button
              v-if="rights.add"
              variant="gradient-primary"
              @click="openAddModal"
              class="action-btn-filter"
              :disabled="dataLoading"
            >
              <b-spinner v-if="dataLoading" small class="mr-1" />
              <feather-icon v-else icon="PlusIcon" size="16" class="mr-1" />
              Add Achievement
            </b-button>
          </div>
        </b-col>
      </b-row>

      <!-- Pagination controls -->
      <div
        class="pagination-controls-top d-flex justify-content-between align-items-center mb-3 flex-wrap"
      >
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <div class="page-size-wrapper">
            <label class="page-size-label">Show:</label>
            <v-select
              :key="'page-size-' + perPage"
              v-model="perPage"
              :options="pageSizeOptions"
              :reduce="(option) => option.value"
              label="label"
              :clearable="false"
              :searchable="false"
              append-to-body
              class="page-size-select-compact"
              @input="onPageSizeChange"
              @option:selected="closePageSizeDropdown"
              ref="pageSizeSelect"
            />
          </div>
        </div>

        <div class="pagination-count-display">
          <span class="count-text">
            Showing <strong>{{ displayedRange.start }}</strong> to
            <strong>{{ displayedRange.end }}</strong> of
            <strong>{{ totalCount }}</strong>
          </span>
        </div>

        <div class="pagination-wrapper-compact">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalCount"
            :per-page="perPage"
            size="sm"
            align="center"
            class="pagination-compact mb-0"
            @change="onPageChange"
          />
        </div>
      </div>

      <div class="table-scroll-container">
        <div
          class="table-scrollbar-top"
          ref="topScrollbar"
          @scroll="syncScroll"
        >
          <div class="table-scrollbar-content"></div>
        </div>

        <div class="table-wrapper" ref="tableWrapper" @scroll="syncScroll">
          <b-table
            class="health-assessments-table achievements-table"
            :items="displayedAchievements"
            :fields="fields"
            :busy="dataLoading"
            :responsive="false"
            hover
            show-empty
            empty-text="No achievements found"
          >
            <template #table-busy>
              <tbody>
                <tr v-for="n in 6" :key="'skeleton-' + n" class="skeleton-row">
                  <td
                    v-for="(field, index) in fields"
                    :key="'skeleton-cell-' + index"
                    class="skeleton-cell"
                  >
                    <div class="skeleton-loader"></div>
                  </td>
                </tr>
              </tbody>
            </template>

            <template #cell(sno)="{ index }">
              <span class="text-muted">{{
                (currentPage - 1) * perPage + index + 1
              }}</span>
            </template>

            <template #cell(studentName)="{ item }">
              <span class="font-weight-bold">{{
                item.studentName || "—"
              }}</span>
            </template>

            <template #cell(sscPerc)="{ item }">
              <span>{{ item.sscPerc != null ? item.sscPerc + "%" : "—" }}</span>
            </template>

            <template #cell(hscPerc)="{ item }">
              <span>{{ item.hscPerc != null ? item.hscPerc + "%" : "—" }}</span>
            </template>

            <template #cell(actions)="{ item }">
              <div class="d-flex align-items-center justify-content-end">
                <b-button
                  v-if="rights.edit"
                  variant="gradient-primary"
                  size="sm"
                  v-b-tooltip.hover.left
                  title="Edit"
                  class="action-btn-table"
                  @click="openEditModal(item)"
                  :disabled="dataLoading"
                >
                  <feather-icon icon="EditIcon" size="16" />
                </b-button>
              </div>
            </template>
          </b-table>
        </div>
      </div>
    </b-card>

    <!-- Modal Form -->
    <b-modal
      v-model="showModal"
      :title="isEdit ? 'Edit Achievement' : 'Add Achievement'"
      size="lg"
      @hidden="resetForm"
      centered
      no-close-on-backdrop
    >
      <b-form @submit.prevent class="mt-1">
        <div class="form-card soft-card">
          <b-row>
            <b-col md="6">
              <b-form-group label-for="studentName">
                <template #label
                  >Student's Name <span class="text-danger">*</span></template
                >
                <b-form-input
                  id="studentName"
                  v-model.trim="form.studentName"
                  placeholder="Student's Name"
                  class="input-compact"
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label-for="fatherName">
                <template #label
                  >Father's Name <span class="text-danger">*</span></template
                >
                <b-form-input
                  id="fatherName"
                  v-model.trim="form.fatherName"
                  placeholder="Father's Name"
                  class="input-compact"
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label-for="batch">
                <template #label
                  >Batch (Year) <span class="text-danger">*</span></template
                >
                <b-form-input
                  id="batch"
                  v-model.trim="form.batch"
                  placeholder="Batch (Year)"
                  class="input-compact"
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label-for="sscPerc" label="SSC %">
                <b-form-input
                  id="sscPerc"
                  v-model.number="form.sscPerc"
                  type="number"
                  step="0.01"
                  placeholder="SSC %"
                  class="input-compact"
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label-for="hscPerc" label="HSC %">
                <b-form-input
                  id="hscPerc"
                  v-model.number="form.hscPerc"
                  type="number"
                  step="0.01"
                  placeholder="HSC %"
                  class="input-compact"
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label-for="field" label="Field">
                <b-form-input
                  id="field"
                  v-model.trim="form.field"
                  placeholder="Field"
                  class="input-compact"
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label-for="institute" label="Institute">
                <b-form-input
                  id="institute"
                  v-model.trim="form.institute"
                  placeholder="Institute"
                  class="input-compact"
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label-for="achievement" label="Achievement">
                <b-form-input
                  id="achievement"
                  v-model.trim="form.achievement"
                  placeholder="Achievement"
                  class="input-compact"
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label-for="currentJob" label="Current Job">
                <b-form-input
                  id="currentJob"
                  v-model.trim="form.currentJob"
                  placeholder="Current Job"
                  class="input-compact"
                />
              </b-form-group>
            </b-col>
          </b-row>
        </div>
      </b-form>

      <template #modal-footer>
        <b-button variant="outline-secondary" @click="showModal = false">
          Cancel
        </b-button>
        <b-button variant="primary" @click="saveAchievement" :disabled="saving">
          <b-spinner v-if="saving" small />
          {{ isEdit ? "Update" : "Save" }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BBadge,
  BPagination,
  BModal,
  BForm,
  BFormGroup,
  BSpinner,
  VBTooltip,
} from "bootstrap-vue";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import { mapActions } from "vuex";

export default {
  name: "AchievementHistory",
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BBadge,
    BPagination,
    BModal,
    BForm,
    BFormGroup,
    BSpinner,
    vSelect,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  data() {
    return {
      rights: {},
      searchQuery: "",
      searchDebounce: null,
      achievements: [],
      dataLoading: false,
      saving: false,
      showModal: false,
      isEdit: false,
      currentPage: 1,
      perPage: 50,
      totalCount: 0,
      totalPages: 1,
      pageSizeOptions: [
        { label: "10", value: 10 },
        { label: "20", value: 20 },
        { label: "30", value: 30 },
        { label: "50", value: 50 },
        { label: "100", value: 100 },
      ],
      fields: [
        { key: "sno", label: "S.No", thClass: "w-sn" },
        { key: "studentName", label: "Student's Name", sortable: false },
        { key: "fatherName", label: "Father's Name", sortable: false },
        { key: "batch", label: "Batch (Year)", sortable: false },
        { key: "sscPerc", label: "SSC %", sortable: false },
        { key: "hscPerc", label: "HSC %", sortable: false },
        { key: "field", label: "Field", sortable: false },
        // { key: "institute", label: "Institute", sortable: false },
        { key: "achievement", label: "Achievement", sortable: false },
        { key: "currentJob", label: "Current Job", sortable: false },
        { key: "actions", label: "Actions" },
      ],
      form: {
        id: 0,
        campusID: this.$store.state.userData.cId,
        sID: 0,
        studentName: "",
        fatherName: "",
        batch: "",
        sscPerc: null,
        hscPerc: null,
        sscFaculty: "",
        hscFaculty: "",
        field: "",
        institute: "",
        achievement: "",
        currentJob: "",
        notes: "",
      },
    };
  },
  computed: {
    displayedRange() {
      if (this.totalCount === 0) return { start: 0, end: 0 };
      const start = (this.currentPage - 1) * this.perPage + 1;
      const end = Math.min(this.currentPage * this.perPage, this.totalCount);
      return { start, end };
    },
    displayedAchievements() {
      const list = this.achievements || [];
      const q = (this.searchQuery || "").trim().toLowerCase();
      if (!q) return list;
      return list.filter((row) => {
        const haystack = [
          row.studentName,
          row.fatherName,
          row.batch,
          row.field,
          row.institute,
          row.achievement,
          row.currentJob,
        ];
        return haystack.some((f) => f && String(f).toLowerCase().includes(q));
      });
    },
  },
  created() {
    if (!this.$store.state.rights[this.$route.name]) {
      this.$router.replace({
        name: "misc-not-authorized",
      });
    } else {
      this.rights = this.$store.state.rights[this.$route.name];
      this.loadAchievements();
    }
  },
  mounted() {
    this.$nextTick(() => this.updateScrollbarWidth());
    window.addEventListener("resize", this.updateScrollbarWidth);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateScrollbarWidth);
    if (this.searchDebounce) clearTimeout(this.searchDebounce);
  },
  methods: {
    ...mapActions({ get: "get", post: "post", put: "put" }),

    buildLoadUrl() {
      return (
        this.$store.state.domain +
        "AchievementHistory/LoadData?db=" +
        this.$store.state.userData.db +
        "&cID=" +
        this.$store.state.userData.cId +
        "&page=" +
        this.currentPage +
        "&pageSize=" +
        this.perPage
      );
    },

    buildPostUrl() {
      return (
        this.$store.state.domain +
        "AchievementHistory?db=" +
        this.$store.state.userData.db
      );
    },

    buildPutUrl() {
      return (
        this.$store.state.domain +
        "AchievementHistory/" +
        this.form.id +
        "?db=" +
        this.$store.state.userData.db
      );
    },

    updateScrollbarWidth() {
      this.$nextTick(() => {
        const tableWrapper = this.$refs.tableWrapper;
        const topScrollbar = this.$refs.topScrollbar;
        const topContent =
          topScrollbar &&
          topScrollbar.querySelector(".table-scrollbar-content");
        if (tableWrapper && topContent) {
          topContent.style.width = `${tableWrapper.scrollWidth}px`;
          topScrollbar.scrollLeft = tableWrapper.scrollLeft;
        }
      });
    },

    syncScroll(event) {
      const tableWrapper = this.$refs.tableWrapper;
      const topScrollbar = this.$refs.topScrollbar;
      if (!tableWrapper || !topScrollbar) return;
      if (event && event.target === tableWrapper) {
        topScrollbar.scrollLeft = tableWrapper.scrollLeft;
      } else if (event && event.target === topScrollbar) {
        tableWrapper.scrollLeft = topScrollbar.scrollLeft;
      }
    },

    onSearchInput() {
      if (this.searchDebounce) clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(() => {
        this.updateScrollbarWidth();
      }, 200);
    },

    onPageSizeChange() {
      this.currentPage = 1;
      this.loadAchievements();
    },

    closePageSizeDropdown() {
      if (
        this.$refs.pageSizeSelect &&
        this.$refs.pageSizeSelect.toggleDropdown
      ) {
        this.$refs.pageSizeSelect.toggleDropdown(false);
      }
    },

    onPageChange() {
      this.loadAchievements();
    },

    async loadAchievements() {
      this.dataLoading = true;
      try {
        const obj = {
          url: this.buildLoadUrl(),
          token: this.$store.state.userData.token,
          returnData: true,
        };
        const response = await this.get(obj);
        if (response === false) {
          this.achievements = [];
          this.totalCount = 0;
          this.$bvToast.toast("Failed to load achievements.", {
            title: "Error",
            variant: "danger",
            solid: true,
          });
          return;
        }
        const payload =
          response && typeof response === "object" ? response : {};
        const container = payload.data !== undefined ? payload.data : payload;
        let records = [];
        if (Array.isArray(container?.data)) {
          records = container.data;
        } else if (Array.isArray(container)) {
          records = container;
        } else if (Array.isArray(payload)) {
          records = payload;
        } else if (Array.isArray(payload.data)) {
          records = payload.data;
        }

        const meta = {
          totalCount:
            payload?.totalCount ??
            container?.totalCount ??
            payload?.data?.totalCount,
          page: payload?.page ?? container?.page,
          pageSize: payload?.pageSize ?? container?.pageSize,
          totalPages: payload?.totalPages ?? container?.totalPages,
        };

        this.achievements = records || [];
        const pageSizeFromApi = Number(meta.pageSize) || this.perPage;
        const totalFromApi = Number(meta.totalCount);
        const totalRecords =
          !Number.isNaN(totalFromApi) && totalFromApi >= 0
            ? totalFromApi
            : records.length;
        this.totalCount = totalRecords;
        if (!Number.isNaN(Number(meta.page)) && Number(meta.page) > 0) {
          this.currentPage = Number(meta.page);
        }
        this.perPage = pageSizeFromApi;
        this.totalPages =
          Number(meta.totalPages) ||
          Math.max(1, Math.ceil(this.totalCount / (this.perPage || 1)));

        this.$nextTick(() => this.updateScrollbarWidth());
      } catch (e) {
        console.error("Load achievements error:", e);
        this.achievements = [];
        this.totalCount = 0;
        this.$bvToast.toast("Failed to load achievements.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.dataLoading = false;
      }
    },

    openAddModal() {
      this.isEdit = false;
      this.resetForm();
      this.showModal = true;
    },

    openEditModal(item) {
      this.isEdit = true;
      this.form = {
        id: Number(item.id) || 0,
        campusID: Number(item.campusID) || this.$store.state.userData.cId,
        sID: Number(item.sID) || 0,
        studentName: item.studentName || "",
        fatherName: item.fatherName || "",
        batch: item.batch || "",
        sscPerc: item.sscPerc != null ? Number(item.sscPerc) : null,
        hscPerc: item.hscPerc != null ? Number(item.hscPerc) : null,
        sscFaculty: item.sscFaculty || "",
        hscFaculty: item.hscFaculty || "",
        field: item.field || "",
        institute: item.institute || "",
        achievement: item.achievement || "",
        currentJob: item.currentJob || "",
        notes: item.notes || "",
      };
      this.showModal = true;
    },

    buildSaveBody() {
      return {
        id: this.form.id || 0,
        campusID: this.isEdit
          ? this.form.campusID || this.$store.state.userData.cId
          : this.$store.state.userData.cId,
        sID: this.form.sID || 0,
        studentName: this.form.studentName || "",
        fatherName: this.form.fatherName || "",
        batch: this.form.batch || "",
        sscPerc: this.form.sscPerc != null ? Number(this.form.sscPerc) || 0 : 0,
        hscPerc: this.form.hscPerc != null ? Number(this.form.hscPerc) || 0 : 0,
        sscFaculty: this.form.sscFaculty || "",
        hscFaculty: this.form.hscFaculty || "",
        field: this.form.field || "",
        institute: this.form.institute || "",
        achievement: this.form.achievement || "",
        currentJob: this.form.currentJob || "",
        notes: this.form.notes || "",
      };
    },

    async saveAchievement() {
      const warn = (msg) => {
        this.$bvToast.toast(msg, {
          title: "Validation",
          variant: "warning",
          solid: true,
        });
      };
      if (!this.form.studentName || !String(this.form.studentName).trim()) {
        return warn("Student's Name is required.");
      }
      if (!this.form.fatherName || !String(this.form.fatherName).trim()) {
        return warn("Father's Name is required.");
      }
      if (!this.form.batch || !String(this.form.batch).trim()) {
        return warn("Batch (Year) is required.");
      }

      this.saving = true;
      try {
        const body = this.buildSaveBody();
        const obj = {
          url: this.isEdit ? this.buildPutUrl() : this.buildPostUrl(),
          body,
          message: this.isEdit
            ? "Achievement updated successfully."
            : "Achievement saved successfully.",
          context: this,
          token: this.$store.state.userData.token,
        };
        const ok = this.isEdit ? await this.put(obj) : await this.post(obj);
        if (ok) {
          this.showModal = false;
          this.loadAchievements();
        }
      } catch (e) {
        console.error("Save achievement error:", e);
        this.$bvToast.toast("Could not save achievement.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.saving = false;
      }
    },

    resetForm() {
      this.form = {
        id: 0,
        campusID: this.$store.state.userData.cId,
        sID: 0,
        studentName: "",
        fatherName: "",
        batch: "",
        sscPerc: null,
        hscPerc: null,
        sscFaculty: "",
        hscFaculty: "",
        field: "",
        institute: "",
        achievement: "",
        currentJob: "",
        notes: "",
      };
    },

    async printReport() {
      const campusMeta = this.$store.state.currentBranch || {};
      const displayName = campusMeta.name || "";
      const branch = campusMeta.branch || "";

      const schoolUpper = displayName.toUpperCase();
      const campusUpper = branch.toUpperCase();

      const now = new Date();
      const periodStr = now.toLocaleString("en-GB", {
        month: "long",
        year: "numeric",
      });
      const generatedStr = now.toLocaleString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });

      const reportTitle = "Student Achievement History Report";
      const recordCount = (this.displayedAchievements || []).length;

      const mainTitleHtml = campusUpper
        ? `<span class="school-name">${this.escapeHtml(
            schoolUpper
          )}</span><span class="title-dash"> — </span><span class="campus-name">${this.escapeHtml(
            campusUpper
          )}</span>`
        : `<span class="school-name">${this.escapeHtml(schoolUpper)}</span>`;

      const metaHtml = `${this.escapeHtml(
        reportTitle
      )} <span class="meta-sep">|</span> <strong>Period:</strong> ${this.escapeHtml(
        periodStr
      )} <span class="meta-sep">|</span> <strong>Records:</strong> ${recordCount} <span class="meta-sep">|</span> <strong>Generated on</strong> ${this.escapeHtml(
        generatedStr
      )}`;

      const thead = `<tr>
        <th>S.No</th>
        <th>Student's Name</th>
        <th>Father's Name</th>
        <th>Batch (Year)</th>
        <th>SSC %</th>
        <th>HSC %</th>
        <th>Field</th>
        <th>Institute</th>
        <th>Achievement</th>
        <th>Current Job</th>
      </tr>`;

      const rows = (this.displayedAchievements || [])
        .map((item, index) => {
          const sscStr = item.sscPerc != null ? item.sscPerc + "%" : "—";
          const hscStr = item.hscPerc != null ? item.hscPerc + "%" : "—";
          return `<tr>
            <td>${index + 1}</td>
            <td>${this.escapeHtml(item.studentName || "—")}</td>
            <td>${this.escapeHtml(item.fatherName || "—")}</td>
            <td>${this.escapeHtml(item.batch || "—")}</td>
            <td>${this.escapeHtml(sscStr)}</td>
            <td>${this.escapeHtml(hscStr)}</td>
            <td>${this.escapeHtml(item.field || "")}</td>
            <td>${this.escapeHtml(item.institute || "")}</td>
            <td>${this.escapeHtml(item.achievement || "")}</td>
            <td>${this.escapeHtml(item.currentJob || "")}</td>
          </tr>`;
        })
        .join("");

      const emptyRow = `<tr><td colspan="10" class="text-center">No records found</td></tr>`;

      const docTitle = "Achievement History Summary";

      const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${this.escapeHtml(
        docTitle
      )}</title>
<style>
  @page { size: A4 landscape; margin: 4mm; }
  body { font-family: Arial, Helvetica, sans-serif; font-size: 11px; color: #111; }
  .print-header { margin-bottom: 0; }
  .print-main-title {
    font-size: 18px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    line-height: 1.25;
    margin: 0 0 10px;
  }
  .print-main-title .school-name { color: #000; }
  .print-main-title .title-dash { color: #000; font-weight: 700; }
  .print-main-title .campus-name { color: #4a4a4a; font-weight: 700; }
  .print-meta {
    font-size: 11px;
    color: #5c5c5c;
    font-weight: 500;
    line-height: 1.5;
    margin: 0 0 10px;
  }
  .print-meta .meta-sep {
    color: #9a9a9a;
    font-weight: 300;
    padding: 0 10px;
  }
  .print-meta strong { color: #444; font-weight: 600; }
  .print-header-rule {
    border: none;
    border-bottom: 3px solid #000;
    margin: 0 0 14px;
    padding: 0;
    height: 0;
  }
  table { width: 100%; border-collapse: collapse; }
  th, td { border: 1px solid #333; padding: 5px 6px; text-align: left; vertical-align: top; }
  th { background: #e8e8e8; font-weight: 600; }
  tr:nth-child(even) td { background: #fafafa; }
  .text-center { text-align: center; }
</style></head><body>
  <header class="print-header">
    <div class="print-main-title">${mainTitleHtml}</div>
    <div class="print-meta">${metaHtml}</div>
    <hr class="print-header-rule" />
  </header>
  <table><thead>${thead}</thead><tbody>${rows || emptyRow}</tbody></table>
</body></html>`;

      const iframe = document.createElement("iframe");
      iframe.setAttribute(
        "style",
        "position:fixed;width:0;height:0;border:0;visibility:hidden;"
      );
      document.body.appendChild(iframe);
      const doc = iframe.contentWindow.document;
      doc.open();
      doc.write(html);
      doc.close();
      setTimeout(() => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } finally {
          setTimeout(() => iframe.remove(), 400);
        }
      }, 200);
    },

    escapeHtml(text) {
      if (text == null) return "";
      return String(text)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@core/scss/vue/libs/vue-select.scss";

/* Mirror style structures from donors.vue */
.health-assessments-list-card {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 20px;
  padding: 1.5rem 1.75rem 1.5rem 1.5rem;
  box-shadow: 0 10px 30px rgba(115, 103, 240, 0.12);
  margin-bottom: 1.5rem;
  position: relative;
  overflow: visible;
  z-index: 1;

  .gradient-line-wrapper {
    position: absolute;
    top: 0;
    left: 0.75rem;
    right: 0.75rem;
    width: calc(100% - 1.5rem);
    height: 5px;
    overflow: hidden;
    border-radius: 10px 10px 0 0;
    z-index: 1;
    pointer-events: none;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      width: 100%;
      height: 5px;
      background: linear-gradient(90deg, #7c5dfa 0%, #9d7aff 50%, #cbb9ff 100%);
      border-radius: 10px 10px 0 0;
    }
  }
}

.table-header {
  padding-bottom: 1rem;
  border-bottom: 2px solid #f0edff;
  margin-bottom: 1rem;

  h5 {
    font-size: 1.25rem;
    font-weight: 800;
    color: #2d1b69;
  }

  small {
    font-size: 0.85rem;
    color: #6b7280;
  }
}

.filter-row-single-line {
  gap: 0.5rem;
}

.no-wrap-row {
  flex-wrap: nowrap;
  @media (max-width: 991.98px) {
    flex-wrap: wrap;
  }
}

.filter-control {
  height: 38px;
  border-radius: 8px;
  border: 1px solid #e0d8ff;
  font-size: 0.875rem;
}

.action-btn-filter {
  height: 38px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.875rem;
}

.action-buttons-group {
  gap: 0.5rem;
  flex-wrap: wrap;
}

.soft-card {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1px solid #e8e5fb;
  border-radius: 12px;
  padding: 1rem 1.25rem;
  box-shadow: 0 10px 30px rgba(115, 103, 240, 0.08);
  margin-bottom: 0.75rem;
}

.input-compact {
  min-height: 40px;
  border-radius: 10px !important;
  border: 1.5px solid #e0d8ff !important;
  padding: 0.45rem 0.75rem !important;
  font-size: 0.95rem !important;
}

.pagination-controls-top {
  padding: 0.75rem 0;
  border-top: 1px solid #f0edff;
  border-bottom: 1px solid #f0edff;
  margin: 1rem 0;
  flex-wrap: wrap;
  gap: 1rem;
}

.page-size-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.page-size-label {
  font-size: 0.8125rem;
  color: #6b7280;
  margin: 0;
  white-space: nowrap;
}

.page-size-select-compact {
  min-width: 80px;
}

.page-size-select-compact ::v-deep .vs__dropdown-toggle {
  height: 32px;
  padding: 0 0.5rem;
  border-radius: 6px;
  border: 1px solid #e0d8ff;
  font-size: 0.8125rem;
}

.pagination-count-display {
  flex: 1;
  text-align: center;
  .count-text {
    font-size: 0.8125rem;
    color: #6b7280;
    strong {
      color: #2d1b69;
    }
  }
}

.pagination-compact {
  margin: 0;
}

.table-scroll-container {
  position: relative;
  width: 100%;
}

.table-scrollbar-top {
  overflow-x: auto;
  overflow-y: hidden;
  width: 100%;
  height: 12px;
  margin-bottom: 8px;
}

.table-scrollbar-top .table-scrollbar-content {
  height: 1px;
}

.table-wrapper {
  overflow-x: auto;
  width: 100%;
}

.health-assessments-table {
  margin-top: 1rem;
  border-radius: 12px;
  min-width: 100%;
  width: max-content;
  border: 1px solid #e5e7eb;
}

.health-assessments-table ::v-deep th {
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  font-weight: 700;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 0.75rem 0.875rem;
  white-space: nowrap;
  border-bottom: 1px solid #e2e8f0;
}

.health-assessments-table ::v-deep td {
  padding: 0.65rem 0.75rem;
  font-size: 0.85rem;
  vertical-align: middle;
}

.skeleton-loader {
  height: 14px;
  border-radius: 4px;
  background: linear-gradient(90deg, #f1f5f9, #e2e8f0, #f1f5f9);
  background-size: 200% 100%;
  animation: shimmer 1.2s ease-in-out infinite;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.action-btn-table {
  border-radius: 8px;
}

.w-sn {
  width: 56px;
}
</style>
